/**
 * Componente para exibir mensagens de localização no chat
 * 
 * Mostra preview visual com pin vermelho, nome e endereço,
 * e link para abrir no Google Maps (estilo WhatsApp)
 */

import { MapPin } from 'lucide-react';

interface LocationMessageBubbleProps {
  /** Latitude da localização */
  latitude: number;
  /** Longitude da localização */
  longitude: number;
  /** Nome do local (opcional) */
  name?: string | null;
  /** Endereço do local (opcional) */
  address?: string | null;
  /** Direção da mensagem (entrada/saída) */
  direction: 'inbound' | 'outbound';
}

export function LocationMessageBubble({
  latitude,
  longitude,
  name,
  address,
  direction,
}: LocationMessageBubbleProps) {
  // URL do Google Maps para abrir a localização
  const mapsUrl = `https://www.google.com/maps?q=${latitude},${longitude}`;

  // Formata as coordenadas para exibição
  const formatCoordinate = (coord: number, isLat: boolean) => {
    const dir = isLat ? (coord >= 0 ? 'N' : 'S') : (coord >= 0 ? 'E' : 'W');
    return `${Math.abs(coord).toFixed(6)}° ${dir}`;
  };

  // Texto de exibição para nome/endereço
  const displayName = name || 'Localização';
  const displayAddress = address || `${formatCoordinate(latitude, true)}, ${formatCoordinate(longitude, false)}`;

  return (
    <div 
      className="w-[140px] overflow-hidden rounded-lg"
      style={{ 
        backgroundColor: '#FFFFFF',
        border: '1px solid #e9edef'
      }}
    >
      {/* Área do mapa visual com fundo escuro - tamanho reduzido 50% */}
      <a 
        href={mapsUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="block relative bg-slate-700 h-[65px] hover:bg-slate-600 transition-colors"
      >
        {/* Pin vermelho centralizado - tamanho reduzido */}
        <div className="absolute inset-0 flex items-center justify-center">
          <MapPin className="h-5 w-5 text-red-500 fill-red-500 drop-shadow-lg" />
        </div>
        
        {/* Logo "Google" no canto inferior esquerdo - menor */}
        <span className="absolute bottom-1 left-1 text-[10px] text-white/70 font-medium">
          Google
        </span>
      </a>

      {/* Informações da localização - cores fixas WhatsApp */}
      <div 
        className="p-1.5 space-y-0"
        style={{ backgroundColor: '#FFFFFF' }}
      >
        {/* Nome como link azul */}
        <a 
          href={mapsUrl} 
          target="_blank" 
          rel="noopener noreferrer"
          className="block text-xs font-medium text-blue-500 hover:underline truncate"
        >
          {displayName}
        </a>
        
        {/* Endereço ou coordenadas */}
        <p 
          className="text-[10px] truncate"
          style={{ color: '#667781' }}
        >
          {displayAddress}
        </p>
      </div>
    </div>
  );
}
